import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { ReportsApi, LoginReportRow, LoginReportView } from '@/services/reports.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/cn';

const ROLE_OPTIONS = ['owner', 'admin', 'manager', 'school_admin', 'teacher', 'head_faculty', 'staff', 'caretaker', 'cashier', 'student', 'parent', 'guardian'];

const columnHelper = createColumnHelper<LoginReportRow>();

const NEVER_COLUMNS = [
  columnHelper.accessor('name', { header: 'Name' }),
  columnHelper.accessor('email', { header: 'Email', cell: (ctx) => ctx.getValue() ?? '—' }),
  columnHelper.accessor('role', { header: 'Role', cell: (ctx) => <Badge tone="slate">{ctx.getValue()}</Badge> }),
];

const LAST_COLUMNS = [
  columnHelper.accessor('name', { header: 'Name' }),
  columnHelper.accessor('email', { header: 'Email', cell: (ctx) => ctx.getValue() ?? '—' }),
  columnHelper.accessor('role', { header: 'Role', cell: (ctx) => <Badge tone="slate">{ctx.getValue()}</Badge> }),
  columnHelper.accessor('lastLoginAt', {
    header: 'Last Login',
    cell: (ctx) =>
      ctx.getValue() ? (
        formatDateTime(ctx.getValue())
      ) : (
        <span className="text-xs uppercase tracking-wide text-amber-600">Never</span>
      ),
  }),
  columnHelper.accessor('lastLoginType', { header: 'Type', cell: (ctx) => ctx.getValue() ?? '—' }),
];

/** Login Report — 'never logged in' roster and 'last login' per user, reading login_logs (new instrumentation, not migrated data). */
export function LoginReportPage() {
  const [view, setView] = useState<LoginReportView>('last');
  const [role, setRole] = useState('');
  const dt = useDataTable({ defaultLimit: 20 });

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, role]);

  const { data, isFetching } = useQuery({
    queryKey: ['login-report', view, role, dt.params],
    queryFn: () =>
      ReportsApi.loginReport({
        view,
        role: role || undefined,
        page: dt.params.page,
        limit: dt.params.limit,
      }),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title="Login Report"
        description="Logins recorded going forward — a freshly provisioned school shows no history until real logins accrue."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
          {(['last', 'never'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium',
                view === v ? 'bg-brand-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
              )}
            >
              {v === 'last' ? 'Last Login' : 'Never Logged In'}
            </button>
          ))}
        </div>
        <Select className="!w-48" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">All roles</option>
          {ROLE_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
      </div>

      <DataTable
        columns={view === 'last' ? LAST_COLUMNS : NEVER_COLUMNS}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage={view === 'last' ? 'No users found.' : 'Every user has logged in at least once.'}
        getRowId={(row) => row.userId}
      />
    </>
  );
}
